import { ChangeEvent, useCallback, useState } from 'react';
import { Box, Button, IconButton, InputBaseProps, Stack, Typography } from '@mui/material';

import { Field, UnderlineInput } from './fields';
import { currencyExact, lineAmount } from './format';
import { LineItem } from './types';

import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';
import ChevronUpIcon from 'modules/common/components/Icons/ChevronUpIcon';
import DocumentTextIcon from 'modules/common/components/Icons/DocumentTextIcon';
import PlusCircleIcon from 'modules/common/components/Icons/PlusCircleIcon';

interface LineItemsEditorProps {
    items: LineItem[];
    hasClient: boolean;
    onChange: (items: LineItem[]) => void;
}

// Item, Quantity, Price, Amount, then the expand chevron — shared by the header and every row.
const COLUMNS = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 90px 110px 120px 40px',
    gap: 2,
    alignItems: 'center',
    px: 2,
};

let itemCounter = 0;
export const newLineItem = (fields: Partial<Omit<LineItem, 'id'>> = {}): LineItem => {
    itemCounter += 1;
    return { id: `item_${itemCounter}`, description: '', quantity: 1, price: 0, ...fields };
};

interface NumberInputProps extends Omit<InputBaseProps, 'value' | 'onChange'> {
    value: number;
    onValue: (value: number) => void;
}

// Keeps the text as typed, so "0" survives on the way to "0.50"; the item only ever gets the number.
// It starts blank for 0 so the placeholder shows.
function NumberInput({ value, onValue, ...props }: Readonly<NumberInputProps>) {
    const [text, setText] = useState(value === 0 ? '' : String(value));
    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setText(e.target.value);
            onValue(Math.max(0, Number(e.target.value) || 0));
        },
        [onValue]
    );

    return <UnderlineInput {...props} type="number" value={text} onChange={handleChange} />;
}

interface LineItemRowProps {
    item: LineItem;
    expanded: boolean;
    onToggle: (id: string) => void;
    onChange: (item: LineItem) => void;
    onRemove: (id: string) => void;
}

function LineItemRow({ item, expanded, onToggle, onChange, onRemove }: Readonly<LineItemRowProps>) {
    const handleToggle = useCallback(() => onToggle(item.id), [item.id, onToggle]);
    const handleRemove = useCallback(() => onRemove(item.id), [item.id, onRemove]);
    const handleQuantity = useCallback((quantity: number) => onChange({ ...item, quantity }), [item, onChange]);
    const handlePrice = useCallback((price: number) => onChange({ ...item, price }), [item, onChange]);
    const name = item.description || 'New item';

    return (
        <Box sx={{ border: 1, borderColor: 'cement.200', borderRadius: 1, mb: 1 }}>
            <Box sx={{ ...COLUMNS, py: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <DocumentTextIcon fontSize="small" />
                    <Typography variant="body2" noWrap color={item.description ? 'text.primary' : 'text.disabled'}>
                        {name}
                    </Typography>
                </Stack>
                <Typography variant="body2" align="right">
                    {item.quantity.toFixed(2)}
                </Typography>
                <Typography variant="body2" align="right">
                    {currencyExact(item.price)}
                </Typography>
                <Typography variant="body2" align="right">
                    {currencyExact(lineAmount(item))}
                </Typography>
                <IconButton
                    size="small"
                    aria-label={`${expanded ? 'Collapse' : 'Edit'} ${name}`}
                    aria-expanded={expanded}
                    onClick={handleToggle}>
                    {expanded ? <ChevronUpIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
                </IconButton>
            </Box>
            {expanded && (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={3}
                    alignItems={{ sm: 'flex-end' }}
                    sx={{ px: 2, pb: 2 }}>
                    <Field label="Description" flex={3}>
                        <UnderlineInput
                            autoFocus={!item.description}
                            placeholder="What was this for?"
                            value={item.description}
                            onChange={(e) => onChange({ ...item, description: e.target.value })}
                            inputProps={{ 'aria-label': 'Item description' }}
                        />
                    </Field>
                    <Field label="Quantity" flex={1}>
                        <NumberInput
                            placeholder="0"
                            value={item.quantity}
                            onValue={handleQuantity}
                            inputProps={{ 'aria-label': 'Quantity', min: 0 }}
                        />
                    </Field>
                    <Field label="Price" flex={1}>
                        <NumberInput
                            placeholder="0.00"
                            value={item.price}
                            onValue={handlePrice}
                            inputProps={{ 'aria-label': 'Price', min: 0, step: '0.01' }}
                        />
                    </Field>
                    <Button variant="text" color="error" size="small" onClick={handleRemove}>
                        Remove
                    </Button>
                </Stack>
            )}
        </Box>
    );
}

export default function LineItemsEditor({ items, hasClient, onChange }: Readonly<LineItemsEditorProps>) {
    // Items pre-filled from the client start closed, like saved rows in the real tool; new ones open.
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

    const handleToggle = useCallback(
        (id: string) =>
            setExpanded((prev) => {
                const next = new Set(prev);
                if (!next.delete(id)) {
                    next.add(id);
                }
                return next;
            }),
        []
    );
    const handleItemChange = useCallback(
        (next: LineItem) => onChange(items.map((item) => (item.id === next.id ? next : item))),
        [items, onChange]
    );
    const handleRemove = useCallback(
        (id: string) => onChange(items.filter((item) => item.id !== id)),
        [items, onChange]
    );
    const addItem = useCallback(
        (fields?: Partial<Omit<LineItem, 'id'>>) => {
            const item = newLineItem(fields);
            setExpanded((prev) => new Set(prev).add(item.id));
            onChange([...items, item]);
        },
        [items, onChange]
    );
    const handleAddItem = useCallback(() => addItem(), [addItem]);
    const handleAddExpense = useCallback(() => addItem({ description: 'Reimbursable expense' }), [addItem]);

    if (!hasClient) {
        return (
            <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Choose your client
                </Typography>
            </Box>
        );
    }

    return (
        <div>
            {items.length > 0 && (
                <Box sx={{ ...COLUMNS, pb: 1 }}>
                    {['Item', 'Quantity', 'Price', 'Amount'].map((heading, i) => (
                        <Typography key={heading} variant="captionSemiBold" align={i === 0 ? 'left' : 'right'}>
                            {heading}
                        </Typography>
                    ))}
                </Box>
            )}
            {items.map((item) => (
                <LineItemRow
                    key={item.id}
                    item={item}
                    expanded={expanded.has(item.id)}
                    onToggle={handleToggle}
                    onChange={handleItemChange}
                    onRemove={handleRemove}
                />
            ))}
            <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<PlusCircleIcon fontSize="small" />}
                    onClick={handleAddItem}>
                    Add Item
                </Button>
                <Button variant="outlined" color="primary" size="small" onClick={handleAddExpense}>
                    Expense
                </Button>
            </Stack>
        </div>
    );
}
